import React, { useCallback, useEffect } from "react";
import { useGet } from "../../api";
import {
  ITestResultWithMerchant,
  ITestResultsResponse,
} from "../../interfaces";
import { Button, Table, Spinner, Form, Row, Col, Badge } from "react-bootstrap";

import { format } from "date-fns";
import { Link } from "react-router";

export const TestResults: React.FC = () => {
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(25);
  const [selectedStatus, setSelectedStatus] = React.useState<string>("");
  const [merchantName, setMerchantName] = React.useState<string>("");
  const [merchantUrl, setMerchantUrl] = React.useState<string>("");
  const [startDate, setStartDate] = React.useState<string>("");
  const [endDate, setEndDate] = React.useState<string>("");

  let queryParams = `page=${page}&limit=${limit}`;
  if (selectedStatus) queryParams += `&status=${selectedStatus}`;
  if (merchantName)
    queryParams += `&merchantName=${encodeURIComponent(merchantName)}`;
  if (merchantUrl)
    queryParams += `&merchantUrl=${encodeURIComponent(merchantUrl)}`;
  if (startDate) queryParams += `&startDate=${startDate}`;
  if (endDate) queryParams += `&endDate=${endDate}`;

  const results = useGet<ITestResultsResponse>(`results?${queryParams}`);

  React.useEffect(() => {
    setPage(1);
  }, [selectedStatus, merchantName, merchantUrl, startDate, endDate]);

  const handleRefresh = useCallback(async () => {
    await results.refetch();
  }, [results]);

  useEffect(() => {
    const interval = setInterval(() => {
      handleRefresh();
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, [handleRefresh]);

  const handleClearFilters = () => {
    setSelectedStatus("");
    setMerchantName("");
    setMerchantUrl("");
    setStartDate("");
    setEndDate("");
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Test Results</h2>
        <div>
          <Button
            variant="outline-primary"
            className="me-2"
            onClick={handleRefresh}
          >
            <i className="bi bi-arrow-clockwise"></i> Refresh
          </Button>
        </div>
      </div>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Merchant Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter merchant name"
              value={merchantName}
              onChange={(e) => setMerchantName(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Merchant URL</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter URL"
              value={merchantUrl}
              onChange={(e) => setMerchantUrl(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="passed">Passed</option>
              <option value="failed">Failed</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleClearFilters}
          >
            <i className="bi bi-x-circle"></i> Clear Filters
          </Button>
          {results.data?.pagination && (
            <span className="ms-3 text-muted">
              Showing {results.data.results.length} of{" "}
              {results.data.pagination.total} results
            </span>
          )}
        </Col>
      </Row>

      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>ID</th>
            <th>Merchant</th>
            <th>Status</th>
            <th>Best Coupon</th>
            <th>Original Total</th>
            <th>Started At</th>
            <th>Duration</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {results.loading ? (
            <tr>
              <td colSpan={8} className="text-center py-4">
                <Spinner animation="border" role="status" className="me-2">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                Loading test results...
              </td>
            </tr>
          ) : results.error ? (
            <tr>
              <td colSpan={8} className="text-center text-danger py-4">
                Error loading test results: {results.error.message}
              </td>
            </tr>
          ) : results?.data?.results?.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center py-4">
                No test results found
              </td>
            </tr>
          ) : (
            results?.data?.results?.map((result: ITestResultWithMerchant) => {
              const duration =
                new Date(result.finishedAt).getTime() -
                new Date(result.startedAt).getTime();
              const durationMinutes = Math.floor(duration / 60000);
              const durationSeconds = Math.floor((duration % 60000) / 1000);

              return (
                <tr key={result._id}>
                  <td>
                    <small className="text-muted">{result._id}</small>
                  </td>
                  <td>
                    <Link to={`/merchants/${result.merchant._id}`}>
                      <strong>{result.merchant.name}</strong>
                    </Link>
                    <br />
                    <small className="text-muted">
                      {result.merchant.homePageUrl}
                    </small>
                    <br />
                    <Badge bg="secondary" className="mt-1">
                      {result.merchant.countryCode}
                    </Badge>
                  </td>
                  <td>
                    {result.status === "passed" ? (
                      <Badge bg="success">
                        <i className="bi bi-check-circle"></i> Passed
                      </Badge>
                    ) : (
                      <Badge bg="danger">
                        <i className="bi bi-x-circle"></i> Failed
                      </Badge>
                    )}
                  </td>
                  <td>
                    {result.bestCoupon ? (
                      <code>{result.bestCoupon}</code>
                    ) : (
                      <span className="text-muted">None</span>
                    )}
                  </td>
                  <td>
                    <strong>
                      {result.currency} {result.originalTotal.toFixed(2)}
                    </strong>
                  </td>
                  <td>
                    {format(new Date(result.startedAt), "yyyy-MM-dd HH:mm:ss")}
                  </td>
                  <td>
                    {durationMinutes}m {durationSeconds}s
                  </td>
                  <td>
                    <Button
                      as={Link as any}
                      variant="outline-primary"
                      size="sm"
                      to={`/tasks/${result.task._id}`}
                      title="View Task"
                    >
                      <i className="bi bi-eye"></i> View Task
                    </Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
        {results.data?.pagination && (
          <tfoot>
            <tr>
              <td colSpan={8} className="text-center">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  className="me-2"
                >
                  <i className="bi bi-chevron-left"></i> Previous
                </Button>
                <span className="mx-2">
                  Page {page} of {results.data.pagination.totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= results.data.pagination.totalPages}
                  className="ms-2"
                >
                  Next <i className="bi bi-chevron-right"></i>
                </Button>
              </td>
            </tr>
          </tfoot>
        )}
      </Table>
    </div>
  );
};
